import { useState, useEffect, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useBillingPlans,
  useChangeTenantBillingPlan,
  useRemoveTenantBillingPlan,
  useDeleteTenant,
  useCreateTenant,
  useTenantDetail,
  useTenants,
  useUpdateTenant,
  usePatchTenantTrial,
  useS3Configs,
  useTaxTypes,
  type BillingPlan,
  type CreatedTenant,
  type TaxType,
  type TenantSummary,
} from "../api/hooks";
import { TenantBillingFields } from "../components/TenantBillingFields";
import { DataTable, createColumnHelper } from "../components/DataTable";
import {
  Button,
  Chip,
  ErrorNotice,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from "../components/ui";
import { IconPlus } from "../components/icons";
import { computeTrialSummary, describeTrialSummary } from "../lib/trial";
import {
  EMPTY_BILLING_VALUES,
  billingValuesFromTenant,
  billingValuesToCreate,
  billingValuesToPatch,
  missingBillingFields,
  type BillingFormValues,
} from "../lib/tenant-fiscal";
import { tenantFormSyncKey } from "../lib/tenant-form-sync";

/**
 * Gestión de tenants (CLAUDE.md Fase 4 + Fase 7, paso 9).
 *
 * Solo super-admin llega a esta pantalla (el menú y la ruta ya lo filtran,
 * ver `permissions.ts`), y el servidor lo vuelve a comprobar en cada ruta
 * (`actor.isSuperAdmin` en `panel.ts`). Un tenant es la corporación cliente
 * de la plataforma; dentro de él puede haber varias empresas/cargadores
 * (`Counterparty`), cada una con sus propios usuarios y vehículos adscritos.
 */

const EMPTY_CREATE = {
  name: "",
  cif: "",
  adminEmail: "",
  adminFullName: "",
  billingPlanId: "",
  periodicity: "MONTHLY",
  trialDays: "",
  s3ConfigId: "",
};

const PERIODICITY_OPTIONS = [
  { value: "MONTHLY", label: "Mensual" },
  { value: "QUARTERLY", label: "Trimestral" },
  { value: "ANNUAL", label: "Anual" },
];

const PERIODICITY_LABELS: Record<string, string> = {
  MONTHLY: "Mensual",
  QUARTERLY: "Trimestral",
  ANNUAL: "Anual",
};

const STATUS_TONE: Record<string, "ok" | "warn" | "danger" | "neutral"> = {
  TRIAL: "neutral",
  ACTIVE: "ok",
  PENDING_PAYMENT: "warn",
  BLOCKED: "danger",
  CANCELLED: "neutral",
};

const STATUS_LABELS: Record<string, string> = {
  TRIAL: "Prueba",
  ACTIVE: "Activa",
  PENDING_PAYMENT: "Pago pendiente",
  BLOCKED: "Bloqueada",
  CANCELLED: "Cancelada",
};

/** Tabs disponibles dentro del modal de gestión. */
type EditTab = "info" | "billing" | "subscription";

export function TenantsPage() {
  const query = useTenants();
  const create = useCreateTenant();
  const update = useUpdateTenant();
  const billingPlans = useBillingPlans();
  const taxTypes = useTaxTypes();
  const s3Configs = useS3Configs();
  const changePlan = useChangeTenantBillingPlan();
  const removePlan = useRemoveTenantBillingPlan();
  const deleteTenant = useDeleteTenant();

  const [createOpen, setCreateOpen] = useState(false);
  const [createValues, setCreateValues] = useState({ ...EMPTY_CREATE });
  // Pestaña «Facturación» del alta. Vacío el tipo fiscal = se preselecciona el predeterminado al abrir.
  const [createBilling, setCreateBilling] = useState<BillingFormValues>({ ...EMPTY_BILLING_VALUES });
  const [createTab, setCreateTab] = useState<"data" | "billing">("data");
  const [created, setCreated] = useState<CreatedTenant | null>(null);
  const [copied, setCopied] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<EditTab>("info");
  const [editValues, setEditValues] = useState({
    name: "",
    cif: "",
    manuallyBlocked: false,
    manuallyBlockedReason: "",
    s3ConfigId: "",
  });
  // Pestaña «Facturación» de la gestión (se rellena al cargar el detalle).
  const [editBilling, setEditBilling] = useState<BillingFormValues>({ ...EMPTY_BILLING_VALUES });
  const [newBillingPlanId, setNewBillingPlanId] = useState("");
  const [newPeriodicity, setNewPeriodicity] = useState("MONTHLY");
  const [trialDateInput, setTrialDateInput] = useState("");
  const [assignTrialDays, setAssignTrialDays] = useState("");

  const detail = useTenantDetail(editingId);
  // El formulario solo se vuelve a rellenar si cambian datos editables: la ficha se refresca sola
  // mientras la suscripción espera un pago y eso no debe borrar lo que se esté escribiendo.
  const formSyncKey = tenantFormSyncKey(detail.data);
  const patchTrial = usePatchTenantTrial(editingId ?? "");

  // Sincronizar el campo de días de trial con el valor actual del tenant.
  // Depende también de editingId para que se dispare al abrir cualquier tenant,
  // incluso si React Query sirve datos cacheados (misma referencia de objeto).
  useEffect(() => {
    if (editingId && detail.data) {
      setAssignTrialDays(
        detail.data.trialDays !== null && detail.data.trialDays !== undefined
          ? String(detail.data.trialDays)
          : "",
      );
      // Sincronizar s3ConfigId con el valor actual del tenant
      setEditValues((prev) => ({
        ...prev,
        s3ConfigId: detail.data!.s3ConfigId ?? "",
      }));
      setEditBilling(billingValuesFromTenant(detail.data));
    } else if (!editingId) {
      setAssignTrialDays("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detail.data se lee, pero solo cuenta formSyncKey
  }, [editingId, formSyncKey]);

  // Alta: solo tipos activos. Edición: además el tipo actual del tenant aunque se haya desactivado,
  // para no perderlo al guardar.
  const activeTaxTypes: TaxType[] = (taxTypes.data ?? []).filter((t) => t.isActive);
  const editTaxTypes: TaxType[] = (taxTypes.data ?? []).filter(
    (t) => t.isActive || t.id === editBilling.taxTypeId,
  );

  const activePlans: BillingPlan[] = (billingPlans.data ?? []).filter((p) => p.isActive);

  // Resumen del trial calculado con el valor GUARDADO (no con lo que se esté escribiendo).
  const trialSummary = detail.data ? computeTrialSummary(detail.data.trialDays, detail.data.createdAt) : null;

  const openCreate = (): void => {
    setCreated(null);
    // El tipo fiscal predeterminado llega preseleccionado; el super-admin puede cambiarlo.
    const defaultTaxType = (taxTypes.data ?? []).find((t) => t.isDefault && t.isActive);
    setCreateValues({ ...EMPTY_CREATE });
    setCreateBilling({ ...EMPTY_BILLING_VALUES, taxTypeId: defaultTaxType?.id ?? "" });
    setCreateTab("data");
    setCreateOpen(true);
  };

  const onCreateSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const trialDays = createValues.trialDays ? parseInt(createValues.trialDays, 10) : undefined;
    create.mutate(
      {
        name: createValues.name.trim(),
        cif: createValues.cif.trim().toUpperCase(),
        adminEmail: createValues.adminEmail.trim(),
        adminFullName: createValues.adminFullName.trim(),
        ...billingValuesToCreate(createBilling),
        billingPlanId: createValues.billingPlanId || undefined,
        periodicity: createValues.periodicity,
        ...(trialDays !== undefined ? { trialDays } : {}),
        ...(createValues.s3ConfigId ? { s3ConfigId: createValues.s3ConfigId } : {}),
      },
      {
        onSuccess: (result) => {
          setCreated(result);
          setCreateOpen(false);
        },
      },
    );
  };

  const queryClient = useQueryClient();

  const openEdit = (tenant: TenantSummary): void => {
    // Eliminar el caché del tenant anterior para forzar fetch fresco
    queryClient.removeQueries({ queryKey: ["tenants", "detail"] });
    setEditValues({
      name: tenant.name,
      cif: tenant.cif ?? "",
      manuallyBlocked: tenant.manuallyBlocked,
      manuallyBlockedReason: "",
      s3ConfigId: "",  // se actualiza en useEffect cuando carga detail.data
    });
    // Los datos de facturación se rellenan en el useEffect cuando carga detail.data.
    setEditBilling({ ...EMPTY_BILLING_VALUES, billingEmail: "", taxTypeId: tenant.taxTypeId ?? "" });
    setNewBillingPlanId("");
    setNewPeriodicity("MONTHLY");
    setActiveTab("info");
    setEditingId(tenant.id);
  };

  const closeEdit = (): void => {
    // Eliminar caché al cerrar para que al reabrir siempre se haga fetch fresco
    queryClient.removeQueries({ queryKey: ["tenants", "detail"] });
    setEditingId(null);
    changePlan.reset();
    update.reset();
  };

  const onEditSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!editingId) return;
    update.mutate(
      {
        id: editingId,
        values: {
          name: editValues.name.trim(),
          cif: editValues.cif.trim().toUpperCase(),
          // Datos de facturación: vacío = quitarlo (correo del administrador / sin impuesto / sin dato).
          ...billingValuesToPatch(editBilling),
          manuallyBlocked: editValues.manuallyBlocked,
          ...(editValues.manuallyBlocked && editValues.manuallyBlockedReason.trim()
            ? { manuallyBlockedReason: editValues.manuallyBlockedReason.trim() }
            : {}),
          // s3ConfigId vacío = no tocar; uuid = asignar nuevo almacén
          ...(editValues.s3ConfigId ? { s3ConfigId: editValues.s3ConfigId } : {}),
        },
      },
      { onSuccess: () => closeEdit() },
    );
  };

  const onChangePlan = (): void => {
    if (!editingId || !newBillingPlanId) return;
    changePlan.mutate(
      { tenantId: editingId, billingPlanId: newBillingPlanId },
      {
        onSuccess: () => {
          setNewBillingPlanId("");
          // Refrescar el detail para mostrar el plan actualizado
          void detail.refetch();
        },
      },
    );
  };

  /** Asignar plan inicial: mismo endpoint que cambiar — la API lo gestiona igual.
   * Si se especifican días de trial, tras crear la suscripción se ajusta trialEndsAt. */
  const onAssignPlan = (): void => {
    if (!editingId || !newBillingPlanId) return;
    const days = assignTrialDays ? parseInt(assignTrialDays, 10) : 0;
    changePlan.mutate(
      { tenantId: editingId, billingPlanId: newBillingPlanId },
      {
        onSuccess: () => {
          setNewBillingPlanId("");
          setNewPeriodicity("MONTHLY");
          if (days > 0) {
            const endsAt = new Date();
            endsAt.setDate(endsAt.getDate() + days);
            patchTrial.mutate(
              { trialEndsAt: endsAt.toISOString() },
              { onSuccess: () => { setAssignTrialDays(""); void detail.refetch(); } },
            );
          } else {
            setAssignTrialDays("");
            void detail.refetch();
          }
        },
      },
    );
  };

  const column = createColumnHelper<TenantSummary>();
  const columns = [
    column.accessor("name", { header: "Nombre" }),
    column.accessor("cif", {
      header: "CIF",
      cell: (info) => <span className="ident">{info.getValue() ?? "—"}</span>,
    }),
    column.accessor("isActive", {
      header: "Estado",
      cell: (info) => (info.getValue() ? <Chip tone="ok">Activo</Chip> : <Chip tone="neutral">Inactivo</Chip>),
    }),
    column.accessor("manuallyBlocked", {
      header: "Bloqueo manual",
      cell: (info) => (info.getValue() ? <Chip tone="danger">Bloqueado</Chip> : <Chip tone="neutral">No</Chip>),
    }),
    column.display({
      id: "edit",
      header: "",
      cell: (info) => <Button onClick={() => openEdit(info.row.original)}>Gestionar</Button>,
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Plataforma"
        title="Empresas (tenants)"
        description="Corporaciones clientes de la plataforma. Cada tenant puede agrupar dentro de sí varias empresas/cargadores, con sus propios usuarios, vehículos y contrapartes."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <IconPlus className="h-4 w-4" />
            Nuevo tenant
          </Button>
        }
      />

      {created ? (
        <div className="mb-4">
          <Notice tone={created.admin.temporaryPassword ? "warn" : "ok"}>
            <p className="font-semibold">
              Tenant «{created.name}» creado con CIF {created.cif}.
            </p>
            {created.admin.temporaryPassword ? (
              <>
                <p className="mt-1">
                  Se ha enviado un email de bienvenida a <strong>{created.admin.email}</strong> con las credenciales.
                  {created.admin.existingAccount ? " (El usuario ya tenía cuenta — se ha reemplazado su contraseña.)" : ""}
                </p>
                <p className="mt-1">
                  Cópiala ahora por si el email no llega — <strong>no se volverá a mostrar</strong>:
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <code className="ident break-all rounded bg-ink-900 px-2 py-1 text-ink-50">
                    {created.admin.temporaryPassword}
                  </code>
                  <Button
                    onClick={() => {
                      void navigator.clipboard.writeText(created.admin.temporaryPassword ?? "").then(() => {
                        setCopied(true);
                        window.setTimeout(() => setCopied(false), 2000);
                      });
                    }}
                  >
                    {copied ? "Copiada" : "Copiar"}
                  </Button>
                </div>
              </>
            ) : (
              <p className="mt-1">
                {created.admin.email} ya tenía cuenta: se le ha añadido acceso a este tenant como ADMIN.
              </p>
            )}
          </Notice>
        </div>
      ) : null}

      {create.isError ? (
        <div className="mb-4">
          <ErrorNotice error={create.error} />
        </div>
      ) : null}

      {query.isError ? (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={query.data?.items ?? []}
          isLoading={query.isLoading}
          emptyMessage="Todavía no hay tenants dados de alta."
        />
      )}

      {/* ───── Modal crear tenant ───── */}
      <Modal open={createOpen} title="Nuevo tenant" onClose={() => setCreateOpen(false)}>
        <form onSubmit={onCreateSubmit} className="space-y-3">
          <div className="flex gap-1 border-b border-ink-200 dark:border-ink-700">
            {(["data", "billing"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setCreateTab(tab)}
                className={[
                  "px-4 py-2 text-sm font-medium transition-colors",
                  createTab === tab
                    ? "border-b-2 border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300"
                    : "text-ink-500 hover:text-ink-700 dark:text-ink-400 dark:hover:text-ink-200",
                ].join(" ")}
              >
                {tab === "data" ? "Datos" : "Facturación"}
              </button>
            ))}
          </div>

          {/* La pestaña Facturación se oculta, no se desmonta, para no perder lo escrito en «Datos». */}
          <div className={createTab === "billing" ? "" : "hidden"}>
            <TenantBillingFields
              idPrefix="t"
              values={createBilling}
              onChange={setCreateBilling}
              taxTypes={activeTaxTypes}
            />
            {createValues.billingPlanId ? (
              <div className="mt-3">
                <Notice tone="warn">
                  Has elegido un plan: se exigen completos domicilio, código postal, población, provincia,
                  correo de facturación, fiscalidad y código de cliente del ERP.
                </Notice>
              </div>
            ) : null}
          </div>

          <div className={createTab === "data" ? "space-y-3" : "hidden"}>
          <p className="text-sm text-ink-600 dark:text-ink-300">
            Se crea el tenant y su primer usuario ADMIN a la vez: un tenant sin ningún administrador quedaría
            inoperable desde el primer momento.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nombre del tenant" htmlFor="t-name">
              <Input
                id="t-name"
                required
                value={createValues.name}
                onChange={(event) => setCreateValues({ ...createValues, name: event.target.value })}
                placeholder="Transportes García SL"
              />
            </Field>
            <Field label="CIF" htmlFor="t-cif">
              <Input
                id="t-cif"
                required
                value={createValues.cif}
                onChange={(event) => setCreateValues({ ...createValues, cif: event.target.value })}
                placeholder="B12345678"
              />
            </Field>
            <Field label="Nombre del administrador" htmlFor="t-admin-name">
              <Input
                id="t-admin-name"
                required
                value={createValues.adminFullName}
                onChange={(event) => setCreateValues({ ...createValues, adminFullName: event.target.value })}
                placeholder="Ana García"
              />
            </Field>
            <Field
              label="Correo del administrador"
              htmlFor="t-admin-email"
              hint="Si ya tiene cuenta en otro tenant, solo se le añade acceso a este."
            >
              <Input
                id="t-admin-email"
                type="email"
                required
                value={createValues.adminEmail}
                onChange={(event) => setCreateValues({ ...createValues, adminEmail: event.target.value })}
                placeholder="ana.garcia@empresa.es"
              />
            </Field>
            <Field label="Plan de facturación (opcional)" htmlFor="t-plan">
              <Select
                id="t-plan"
                value={createValues.billingPlanId}
                onChange={(event) => setCreateValues({ ...createValues, billingPlanId: event.target.value })}
                disabled={billingPlans.isLoading}
              >
                <option value="">
                  {billingPlans.isLoading ? "Cargando planes…" : "Sin plan (asignar después)"}
                </option>
                {activePlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} — {Number(plan.basePrice).toFixed(2)} {plan.currency}/mes
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Periodicidad" htmlFor="t-periodicity">
              <Select
                id="t-periodicity"
                required
                value={createValues.periodicity}
                onChange={(event) => setCreateValues({ ...createValues, periodicity: event.target.value })}
              >
                {PERIODICITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Días de prueba (opcional)"
              htmlFor="t-trial"
              hint="Deja vacío para usar el valor por defecto del plan."
            >
              <Input
                id="t-trial"
                type="number"
                min="0"
                max="365"
                value={createValues.trialDays}
                onChange={(event) => setCreateValues({ ...createValues, trialDays: event.target.value })}
                placeholder="14"
              />
            </Field>
          </div>

          {/* Almacén S3 — fuera del grid para ancho completo */}
          <Field label="Almacén S3" htmlFor="t-s3config" hint="Si no se elige, el tenant usará el almacén por defecto de la plataforma.">
            <Select
              id="t-s3config"
              value={createValues.s3ConfigId}
              onChange={(event) => setCreateValues({ ...createValues, s3ConfigId: event.target.value })}
              disabled={s3Configs.isLoading}
            >
              <option value="">
                {s3Configs.isLoading ? "Cargando almacenes…" : "— Por defecto de la plataforma —"}
              </option>
              {(s3Configs.data ?? []).map((cfg) => (
                <option key={cfg.id} value={cfg.id}>
                  {cfg.name} ({cfg.bucket}){cfg.isDefault ? " ★ por defecto" : ""}
                </option>
              ))}
            </Select>
          </Field>

          </div>

          {create.isError ? <ErrorNotice error={create.error} /> : null}

          <div className="flex gap-2">
            <Button
              type="submit"
              variant="primary"
              disabled={create.isPending}
            >
              {create.isPending ? "Creando…" : "Crear tenant"}
            </Button>
            <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          </div>
        </form>
      </Modal>

      {/* ───── Modal gestionar tenant ───── */}
      <Modal
        open={editingId !== null}
        title={detail.data ? `Gestionar · ${detail.data.name}` : "Gestionar tenant"}
        onClose={closeEdit}
      >
        {detail.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner label="Cargando datos del tenant…" />
          </div>
        ) : detail.isError ? (
          <ErrorNotice error={detail.error} onRetry={() => void detail.refetch()} />
        ) : detail.data ? (
          <>
            {/* ── Pestañas ── */}
            <div className="mb-4 flex gap-1 border-b border-ink-200 dark:border-ink-700">
              {(["info", "billing", "subscription"] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={[
                    "px-4 py-2 text-sm font-medium transition-colors",
                    activeTab === tab
                      ? "border-b-2 border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300"
                      : "text-ink-500 hover:text-ink-700 dark:text-ink-400 dark:hover:text-ink-200",
                  ].join(" ")}
                >
                  {tab === "info" ? "Información" : tab === "billing" ? "Facturación" : "Suscripción y plan"}
                </button>
              ))}
            </div>

            {/* ══════════════ TAB: INFO ══════════════ */}
            {activeTab === "info" ? (
              <div className="space-y-4">
                {/* Contadores de recursos */}
                <div className="grid grid-cols-3 gap-3 rounded-lg border border-ink-200 p-4 text-center dark:border-ink-700 sm:grid-cols-5">
                  {(
                    [
                      { label: "Usuarios", value: detail.data.counts.users },
                      { label: "Vehículos", value: detail.data.counts.vehicles },
                      { label: "Contrapartes", value: detail.data.counts.counterparties },
                      { label: "Plantillas", value: detail.data.counts.templates },
                      { label: "DeCA docs", value: detail.data.counts.documents },
                    ] as const
                  ).map(({ label, value }) => (
                    <div key={label}>
                      <p className="num text-2xl font-bold text-ink-900 dark:text-ink-50">{value}</p>
                      <p className="micro-label mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>

                {/* Formulario de edición */}
                <form onSubmit={onEditSubmit} className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Nombre" htmlFor="te-name">
                      <Input
                        id="te-name"
                        required
                        value={editValues.name}
                        onChange={(event) => setEditValues({ ...editValues, name: event.target.value })}
                      />
                    </Field>
                    <Field label="CIF" htmlFor="te-cif">
                      <Input
                        id="te-cif"
                        required
                        value={editValues.cif}
                        onChange={(event) => setEditValues({ ...editValues, cif: event.target.value })}
                      />
                    </Field>
                  </div>

                  <Field label="Almacén S3" htmlFor="te-s3config" hint="El almacén donde se guardan los PDFs de este tenant. Vacío = usar el por defecto de la plataforma.">
                    <Select
                      id="te-s3config"
                      value={editValues.s3ConfigId}
                      onChange={(event) => setEditValues({ ...editValues, s3ConfigId: event.target.value })}
                      disabled={s3Configs.isLoading}
                    >
                      <option value="">
                        {s3Configs.isLoading
                          ? "Cargando almacenes…"
                          : detail.data?.s3ConfigId
                            ? "— Sin cambiar (mantener actual) —"
                            : "— Por defecto de la plataforma —"}
                      </option>
                      {(s3Configs.data ?? []).map((cfg) => (
                        <option key={cfg.id} value={cfg.id}>
                          {cfg.name} ({cfg.bucket}){cfg.isDefault ? " ★ por defecto" : ""}
                        </option>
                      ))}
                    </Select>
                    {detail.data?.s3ConfigId && (
                      <p className="mt-1 text-xs text-ink-500">
                        Asignado actualmente: <strong>{(s3Configs.data ?? []).find((c) => c.id === detail.data?.s3ConfigId)?.name ?? detail.data.s3ConfigId}</strong>
                      </p>
                    )}
                  </Field>

                  <label className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200">
                    <input
                      type="checkbox"
                      checked={editValues.manuallyBlocked}
                      onChange={(event) => setEditValues({ ...editValues, manuallyBlocked: event.target.checked })}
                    />
                    Bloqueado manualmente (independiente del bloqueo automático por impago)
                  </label>

                  {editValues.manuallyBlocked ? (
                    <Field label="Motivo del bloqueo (opcional)" htmlFor="te-reason">
                      <Textarea
                        id="te-reason"
                        rows={2}
                        value={editValues.manuallyBlockedReason}
                        onChange={(event) =>
                          setEditValues({ ...editValues, manuallyBlockedReason: event.target.value })
                        }
                      />
                    </Field>
                  ) : null}

                  {update.isError ? <ErrorNotice error={update.error} /> : null}

                  <div className="flex gap-2 pt-1">
                    <Button type="submit" variant="primary" disabled={update.isPending}>
                      {update.isPending ? "Guardando…" : "Guardar cambios"}
                    </Button>
                    <Button type="button" onClick={closeEdit}>
                      Cerrar
                    </Button>
                  </div>
                </form>
              </div>
            ) : null}

            {/* ══════════════ TAB: FACTURACIÓN ══════════════ */}
            {activeTab === "billing" ? (
              <form onSubmit={onEditSubmit} className="space-y-3">
                <TenantBillingFields
                  idPrefix="te"
                  values={editBilling}
                  onChange={setEditBilling}
                  taxTypes={editTaxTypes}
                />
                {missingBillingFields(editBilling).length > 0 ? (
                  <Notice tone="warn">
                    Para asignar un plan o quitar la prueba faltan: {missingBillingFields(editBilling).join(", ")}.
                  </Notice>
                ) : null}
                <p className="text-xs text-ink-500 dark:text-ink-400">
                  Si cambias el tipo fiscal afecta a las facturas futuras (las ya emitidas no se tocan).
                </p>
                {update.isError ? <ErrorNotice error={update.error} /> : null}
                <div className="flex gap-2 pt-1">
                  <Button type="submit" variant="primary" disabled={update.isPending}>
                    {update.isPending ? "Guardando…" : "Guardar cambios"}
                  </Button>
                  <Button type="button" onClick={closeEdit}>
                    Cerrar
                  </Button>
                </div>
              </form>
            ) : null}

            {/* ══════════════ TAB: SUSCRIPCIÓN ══════════════ */}
            {activeTab === "subscription" ? (
              <div className="space-y-4">
                {detail.data.subscription ? (
                  <>
                    {/* Tarjeta de suscripción actual */}
                    <div className="rounded-lg border border-ink-200 p-4 dark:border-ink-700">
                      <p className="micro-label mb-3">Plan actual</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-base font-semibold text-ink-900 dark:text-ink-50">
                          {detail.data.subscription.billingPlanName}
                        </span>
                        <Chip tone={STATUS_TONE[detail.data.subscription.status] ?? "neutral"}>
                          {STATUS_LABELS[detail.data.subscription.status] ?? detail.data.subscription.status}
                        </Chip>
                        <span className="text-sm text-ink-500 dark:text-ink-400">
                          · {PERIODICITY_LABELS[detail.data.subscription.periodicity] ?? detail.data.subscription.periodicity}
                        </span>
                      </div>

                      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                        {detail.data.subscription.trialEndsAt ? (
                          <div>
                            <dt className="micro-label">Fin de prueba</dt>
                            <dd className="num mt-0.5">
                              {new Date(detail.data.subscription.trialEndsAt).toLocaleDateString("es-ES")}
                            </dd>
                          </div>
                        ) : null}
                        <div>
                          <dt className="micro-label">Inicio del período</dt>
                          <dd className="num mt-0.5">
                            {new Date(detail.data.subscription.currentPeriodStart).toLocaleDateString("es-ES")}
                          </dd>
                        </div>
                        <div>
                          <dt className="micro-label">Fin del período</dt>
                          <dd className="num mt-0.5">
                            {new Date(detail.data.subscription.currentPeriodEnd).toLocaleDateString("es-ES")}
                          </dd>
                        </div>
                        <div>
                          <dt className="micro-label">Renovación auto.</dt>
                          <dd className="mt-0.5">
                            {detail.data.subscription.autoRenew ? (
                              <Chip tone="ok">Sí</Chip>
                            ) : (
                              <Chip tone="neutral">No</Chip>
                            )}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Gestión del trial */}
                    <div className="rounded-lg border border-ink-200 p-4 dark:border-ink-700">
                      <p className="micro-label mb-3">Gestión del trial</p>
                      {detail.data.subscription.trialEndsAt ? (
                        <p className="mb-3 text-sm text-ink-600 dark:text-ink-300">
                          Trial activo hasta el{" "}
                          <strong className="num">
                            {new Date(detail.data.subscription.trialEndsAt).toLocaleDateString("es-ES")}
                          </strong>
                          . Puedes extender la fecha o activar el tenant inmediatamente.
                        </p>
                      ) : (
                        <p className="mb-3 text-sm text-ink-600 dark:text-ink-300">
                          Este tenant no está en período de prueba (
                          <strong>
                            {STATUS_LABELS[detail.data.subscription.status] ?? detail.data.subscription.status}
                          </strong>
                          ). Puedes iniciar un nuevo trial asignando una fecha futura.
                        </p>
                      )}
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="flex-1 min-w-[200px]">
                          <Field label="Nueva fecha fin de trial" htmlFor="te-trial-date">
                            <Input
                              id="te-trial-date"
                              type="date"
                              value={trialDateInput}
                              onChange={(e) => setTrialDateInput(e.target.value)}
                              min={new Date().toISOString().slice(0, 10)}
                              disabled={patchTrial.isPending}
                            />
                          </Field>
                        </div>
                        <Button
                          variant="primary"
                          onClick={() => {
                            if (!trialDateInput || !editingId) return;
                            patchTrial.mutate(
                              { trialEndsAt: new Date(trialDateInput).toISOString() },
                              {
                                onSuccess: () => {
                                  setTrialDateInput("");
                                  void detail.refetch();
                                },
                              },
                            );
                          }}
                          disabled={!trialDateInput || patchTrial.isPending}
                        >
                          {patchTrial.isPending ? "Guardando…" : "Extender trial"}
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (
                              !editingId ||
                              !window.confirm(
                                "¿Activar este tenant ahora? El trial terminará y el status pasará a Activa.",
                              )
                            )
                              return;
                            patchTrial.mutate(
                              { trialEndsAt: null },
                              {
                                onSuccess: () => void detail.refetch(),
                              },
                            );
                          }}
                          disabled={patchTrial.isPending || !detail.data.subscription.trialEndsAt}
                        >
                          Activar ya
                        </Button>
                      </div>
                      {patchTrial.isSuccess && !patchTrial.isPending ? (
                        <p className="mt-2 text-sm text-ok-700 dark:text-ok-400">✓ Trial actualizado.</p>
                      ) : null}
                      {patchTrial.isError ? (
                        <div className="mt-2">
                          <ErrorNotice error={patchTrial.error} />
                        </div>
                      ) : null}
                    </div>

                    {/* Quitar plan */}
                    <div className="rounded-lg border border-red-200 p-4 dark:border-red-800">
                      <p className="micro-label mb-3">Quitar plan</p>
                      <p className="mb-3 text-sm text-ink-600 dark:text-ink-300">
                        Elimina la suscripción activa. El tenant quedará sin plan asignado y
                        podrás asignarle uno nuevo desde esta misma pantalla.
                      </p>
                      <Button
                        variant="danger"
                        onClick={() => {
                          if (
                            editingId &&
                            window.confirm(
                              "¿Seguro que quieres quitar el plan de este tenant? La suscripción quedará eliminada.",
                            )
                          ) {
                            removePlan.mutate(editingId, {
                              onSuccess: () => void detail.refetch(),
                            });
                          }
                        }}
                        disabled={removePlan.isPending}
                      >
                        {removePlan.isPending ? "Quitando…" : "Quitar plan"}
                      </Button>
                      {removePlan.isError ? (
                        <div className="mt-2">
                          <ErrorNotice error={removePlan.error} />
                        </div>
                      ) : null}
                    </div>

                    {/* Cambiar a otro plan */}
                    <div className="rounded-lg border border-ink-200 p-4 dark:border-ink-700">
                      <p className="micro-label mb-3">Cambiar plan</p>
                      <p className="mb-3 text-sm text-ink-600 dark:text-ink-300">
                        El cambio es inmediato: el nuevo plan queda activo desde ahora. Si el tenant
                        está en período de prueba, el trial se conserva.
                      </p>
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <Field label="Nuevo plan" htmlFor="te-change-plan">
                            <Select
                              id="te-change-plan"
                              value={newBillingPlanId}
                              onChange={(e) => setNewBillingPlanId(e.target.value)}
                              disabled={billingPlans.isLoading || changePlan.isPending}
                            >
                              <option value="">
                                {billingPlans.isLoading ? "Cargando planes…" : "Selecciona un plan distinto…"}
                              </option>
                              {(billingPlans.data ?? [])
                                .filter(
                                  (p) => p.isActive && p.id !== detail.data?.subscription?.billingPlanId,
                                )
                                .map((plan) => (
                                  <option key={plan.id} value={plan.id}>
                                    {plan.name} — {Number(plan.basePrice).toFixed(2)} {plan.currency}/mes
                                  </option>
                                ))}
                            </Select>
                          </Field>
                        </div>
                        <Button
                          variant="primary"
                          onClick={onChangePlan}
                          disabled={!newBillingPlanId || changePlan.isPending}
                        >
                          {changePlan.isPending ? "Cambiando…" : "Aplicar"}
                        </Button>
                      </div>
                      {changePlan.isSuccess && !newBillingPlanId ? (
                        <p className="mt-2 text-sm text-ok-700 dark:text-ok-400">
                          ✓ Plan actualizado correctamente.
                        </p>
                      ) : null}
                      {changePlan.isError ? (
                        <div className="mt-2">
                          <ErrorNotice error={changePlan.error} />
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : (
                  /* Sin suscripción: formulario para asignar plan inicial */
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
                    <p className="mb-1 font-medium text-amber-800 dark:text-amber-200">
                      Sin suscripción asignada
                    </p>
                    <p className="mb-4 text-sm text-amber-700 dark:text-amber-300">
                      Este tenant no tiene ningún plan de facturación activo. Asigna uno ahora
                      para que pueda operar con normalidad.
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Plan de facturación" htmlFor="te-assign-plan">
                        <Select
                          id="te-assign-plan"
                          value={newBillingPlanId}
                          onChange={(e) => setNewBillingPlanId(e.target.value)}
                          disabled={billingPlans.isLoading || changePlan.isPending}
                        >
                          <option value="">
                            {billingPlans.isLoading ? "Cargando planes…" : "Selecciona un plan"}
                          </option>
                          {activePlans.map((plan) => (
                            <option key={plan.id} value={plan.id}>
                              {plan.name} — {Number(plan.basePrice).toFixed(2)} {plan.currency}/mes
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Field label="Periodicidad" htmlFor="te-assign-periodicity">
                        <Select
                          id="te-assign-periodicity"
                          value={newPeriodicity}
                          onChange={(e) => setNewPeriodicity(e.target.value)}
                          disabled={changePlan.isPending}
                        >
                          {PERIODICITY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Select>
                      </Field>
                    </div>
                    <div className="mt-3">
                      <Button
                        variant="primary"
                        onClick={onAssignPlan}
                        disabled={!newBillingPlanId || changePlan.isPending}
                      >
                        {changePlan.isPending ? "Asignando…" : "Asignar plan"}
                      </Button>
                    </div>
                    {changePlan.isError ? (
                      <div className="mt-2">
                        <ErrorNotice error={changePlan.error} />
                      </div>
                    ) : null}
                  </div>
                )}

                {/* ══ Días de trial ══ */}
                <div className="rounded-lg border border-ink-200 p-4 dark:border-ink-700">
                  <p className="micro-label mb-1">Días de trial</p>
                  <p className="mb-3 text-sm text-ink-600 dark:text-ink-300">
                    Total de días de prueba <strong>contados desde el alta del tenant</strong>, no desde hoy ni
                    sumados a los ya consumidos: pasar de 20 a 25 días añade 5 días, no 25. <strong>0</strong> = sin
                    trial. Al asignar un plan se calculará <code>trialEndsAt</code> con este valor. Si el tenant ya
                    tiene suscripción, este campo no cambia el trial en curso: usa «Nueva fecha fin de trial».
                  </p>
                  {trialSummary ? (
                    <p className="mb-3 text-sm font-medium text-ink-700 dark:text-ink-200">
                      {describeTrialSummary(trialSummary)}
                      {detail.data?.subscription?.trialEndsAt
                        ? " (con suscripción manda su fecha de fin de trial)"
                        : ""}
                    </p>
                  ) : null}
                  <div className="flex items-end gap-3">
                    <div className="w-36">
                      <Field label="Días" htmlFor="te-trial-days">
                        <Input
                          id="te-trial-days"
                          type="number"
                          min="0"
                          max="3650"
                          placeholder="ej. 15"
                          value={assignTrialDays}
                          onChange={(e) => setAssignTrialDays(e.target.value)}
                          disabled={update.isPending}
                        />
                      </Field>
                    </div>
                    <Button
                      variant="primary"
                      disabled={update.isPending}
                      onClick={() => {
                        if (!editingId) return;
                        const parsed = assignTrialDays !== "" ? parseInt(assignTrialDays, 10) : null;
                        update.mutate(
                          { id: editingId, values: { trialDays: parsed } },
                          { onSuccess: () => { void detail.refetch(); } },
                        );
                      }}
                    >
                      {update.isPending ? "Guardando…" : "Guardar"}
                    </Button>
                  </div>
                  {update.isError ? <ErrorNotice error={update.error} /> : null}
                  {detail.data?.trialDays !== undefined && detail.data.trialDays !== null ? (
                    <p className="mt-1 text-xs text-ink-400">
                      Valor actual: <strong>{detail.data.trialDays}</strong> días
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-ink-400">
                      Valor actual: defecto de plataforma
                    </p>
                  )}
                </div>

                {/* ══ Zona de peligro ══ */}
                <div className="rounded-lg border border-red-300 p-4 dark:border-red-800">
                  <p className="micro-label mb-1 text-red-700 dark:text-red-400">Zona de peligro</p>
                  <p className="mb-3 text-sm text-ink-600 dark:text-ink-300">
                    Elimina este tenant y <strong>todos sus datos</strong>: documentos, suscripción,
                    facturas, usuarios vinculados, etc. Esta acción es irreversible.
                  </p>
                  <Button
                    variant="danger"
                    disabled={deleteTenant.isPending}
                    onClick={() => {
                      const tenantName = detail.data?.name ?? "";
                      const typed = window.prompt(
                        `Para confirmar, escribe el nombre exacto del tenant:\n\n"${tenantName}"`,
                      );
                      if (typed === null) return; // canceló
                      if (typed !== tenantName) {
                        window.alert("El nombre no coincide. Operación cancelada.");
                        return;
                      }
                      deleteTenant.mutate(editingId ?? "", {
                        onSuccess: () => closeEdit(),
                      });
                    }}
                  >
                    {deleteTenant.isPending ? "Eliminando…" : "Eliminar tenant"}
                  </Button>
                  {deleteTenant.isError ? (
                    <div className="mt-2">
                      <ErrorNotice error={deleteTenant.error} />
                    </div>
                  ) : null}
                </div>

                <div className="flex justify-end pt-1">
                  <Button type="button" onClick={closeEdit}>
                    Cerrar
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </Modal>
    </>
  );
}
